import React, { useRef } from "react";
import { Box, Chip, TextField, Typography } from "@mui/material";
import { colors } from "../theme/designSystem";

/** Variáveis aceitas pelo servidor (RN-551g). Manter igual a SininhoMessageTemplate.Variables. */
export const SININHO_VARIABLES: { key: string; label: string; sample: string }[] = [
  { key: "destinatario", label: "quem recebe (primeiro nome)", sample: "Ana" },
  { key: "paciente", label: "paciente", sample: "João da Silva" },
  { key: "paciente_primeiro_nome", label: "primeiro nome do paciente", sample: "João" },
  { key: "profissional", label: "profissional", sample: "Ana Claudia Tavares" },
  { key: "profissional_primeiro_nome", label: "primeiro nome do profissional", sample: "Ana" },
  { key: "data", label: "data", sample: "27/09" },
  { key: "dia_semana", label: "dia da semana", sample: "sábado" },
  { key: "hora", label: "hora", sample: "14:00" },
  { key: "servico", label: "serviço", sample: "Sessão de Psicoterapia" },
  { key: "sala", label: "sala", sample: "Sala 3" },
  { key: "status", label: "status", sample: "" },
];

export const SININHO_MAX_LENGTH = 500;

/** Texto padrão do servidor quando o campo fica vazio. */
export const SININHO_DEFAULT_MESSAGE =
  "🔔 Oi, {destinatario}!\n{paciente} — {status}\n📅 {dia_semana}, {data} às {hora} · {sala}";

const VARIABLE_PATTERN = /\{([^{}\r\n]*)\}/g;

export const unknownSininhoVariables = (text: string): string[] => {
  const known = new Set(SININHO_VARIABLES.map((v) => v.key));
  const found = Array.from(text.matchAll(VARIABLE_PATTERN)).map((m) => m[1].trim());
  return Array.from(new Set(found.filter((v) => !known.has(v.toLowerCase()))));
};

/** Mesma montagem do servidor: variável vazia some, e a linha que ficar vazia sai. */
const renderPreview = (text: string, statusName: string): string => {
  const values = new Map(SININHO_VARIABLES.map((v) => [v.key, v.key === "status" ? statusName : v.sample]));
  return (text.trim() || SININHO_DEFAULT_MESSAGE)
    .replace(VARIABLE_PATTERN, (all, name: string) => {
      const key = name.trim().toLowerCase();
      return values.has(key) ? values.get(key) ?? "" : all;
    })
    .split("\n")
    .map((l) => l.trimEnd().replace(/[·,\-—\s]+$/, ""))
    .filter((l) => l.length > 0)
    .join("\n");
};

interface Props {
  value: string;
  onChange: (value: string) => void;
  statusName: string;
}

/** Mensagem do aviso do Sininho por status (RN-551g): texto livre com variáveis e pré-visualização. */
const SininhoMessageEditor: React.FC<Props> = ({ value, onChange, statusName }) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const unknown = unknownSininhoVariables(value);

  const insert = (key: string) => {
    const token = `{${key}}`;
    const el = inputRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`.slice(0, SININHO_MAX_LENGTH);
    onChange(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <TextField
        label="Mensagem do aviso"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputRef={inputRef}
        multiline
        minRows={3}
        size="small"
        fullWidth
        placeholder={SININHO_DEFAULT_MESSAGE}
        inputProps={{ maxLength: SININHO_MAX_LENGTH }}
        InputLabelProps={{ shrink: true }}
        error={unknown.length > 0}
        helperText={unknown.length > 0
          ? `Variável desconhecida: ${unknown.map((v) => `{${v}}`).join(", ")}`
          : `Vazio = texto padrão. Emojis e quebras de linha valem. ${value.length}/${SININHO_MAX_LENGTH}`}
      />
      <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
        Toque numa variável para inserir no texto. No envio ela vira o dado do agendamento; sem dado (ex.: sem sala), some.
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {SININHO_VARIABLES.map((v) => (
          <Chip
            key={v.key}
            size="small"
            label={`{${v.key}}`}
            title={v.label}
            onClick={() => insert(v.key)}
            sx={{ fontFamily: "monospace", fontSize: "0.72rem", cursor: "pointer" }}
          />
        ))}
      </Box>
      <Box sx={{ border: `1px solid ${colors.border}`, borderRadius: "8px", p: 1.25, bgcolor: "#f8f9fa" }}>
        <Typography sx={{ fontSize: "0.72rem", color: colors.textSecondary, mb: 0.5 }}>Pré-visualização (dados de exemplo)</Typography>
        <Typography sx={{ fontSize: "0.85rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {renderPreview(value, statusName)}
        </Typography>
      </Box>
    </Box>
  );
};

export default SininhoMessageEditor;
